import { createSlice } from "@reduxjs/toolkit";

import { getMyPermissions } from "./myPermissionsAPI";

/**
 * The signed-in account's permission set.
 *
 * Fetched once per session and again whenever the token is refreshed, so a role granted mid-session
 * appears as soon as the token carrying it does. Nothing here is security: the server decides. This
 * exists so the portal can avoid offering work the person cannot complete.
 */
const initialState = {
  status: "idle",
  error: null,
  permissions: [],
  roles: [],
  primaryRole: null,
  accountType: null,
  username: null,
  userId: null,
};

export const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    setLoading: (state) => {
      state.status = "loading";
      state.error = null;
    },
    setPermissions: (state, action) => {
      const payload = action.payload ?? {};
      state.status = "succeeded";
      state.error = null;
      state.permissions = payload.permissions ?? [];
      state.roles = payload.roles ?? [];
      state.primaryRole = payload.primaryRole ?? null;
      state.accountType = payload.accountType ?? null;
      state.username = payload.username ?? null;
      state.userId = payload.userId ?? null;
    },
    /**
     * A failure keeps the previous set rather than emptying it. Emptying would revoke the menu from
     * somebody mid-task because one request timed out, and the set it replaced was already correct.
     */
    setError: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
  },
});

export const { setLoading, setPermissions, setError } = permissionsSlice.actions;

/**
 * Falls back to the initial state when the slice is absent rather than throwing.
 *
 * Dozens of component tests build a store holding only the slice they are testing, and a gate added
 * to one of those components should not break a test that has nothing to do with permissions. The
 * fallback reads as `status: "idle"`, which `can()` treats as permit — the same answer as the flag
 * being off, so an absent slice degrades to today's behaviour instead of hiding every control.
 */
export const selectPermissions = (state) => state.permissions ?? initialState;

export const handleGetMyPermissions = () => async (dispatch) => {
  dispatch(setLoading());
  try {
    const response = await getMyPermissions();
    dispatch(setPermissions(response.data));
  } catch (error) {
    dispatch(setError(errorMessage(error)));
  }
};

/**
 * Kept narrow on purpose. The portal cannot do anything useful about any of these, so the message
 * exists to be recognisable in a bug report rather than actionable on screen.
 */
export const errorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 404) {
    return "The permissions endpoint is not available on this environment.";
  }
  if (status === 401 || status === 403) {
    return "This account was refused when reading its own permissions.";
  }
  return error?.response?.data?.message ?? "Could not read this account's permissions.";
};

export default permissionsSlice.reducer;
